import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Lock, ArrowLeft, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { token, user } = response.data;

      // Check if user is an employee
      if (user.role !== 'employee') {
        toast.error('This portal is only for employees. Please use the staff login.');
        setLoading(false);
        return;
      }

      // Store employee-specific token
      localStorage.setItem('employeeToken', token);
      localStorage.setItem('employeeUser', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      toast.success(`Welcome, ${user.name}!`);
      navigate('/employee/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-gold/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-brand-navy/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Back to Home */}
        <Link
          to="/"
          className="inline-flex items-center text-slate-600 hover:text-brand-navy mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="border-0 shadow-2xl">
          {/* Header */}
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-brand-navy rounded-xl flex items-center justify-center shadow-lg mb-4">
              <User className="h-8 w-8 text-brand-gold" />
            </div>
            <CardTitle className="text-2xl font-heading text-brand-navy">Employee Portal</CardTitle>
            <CardDescription className="text-slate-500">
              Sign in to access your attendance and salary slips
            </CardDescription>
          </CardHeader>

          {/* Form */}
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-700">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="username"
                    data-testid="employee-username-input"
                    placeholder="Enter your username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="pl-10 border-slate-200 focus:border-brand-gold focus:ring-brand-gold"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="employee-password-input"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 border-slate-200 focus:border-brand-gold focus:ring-brand-gold"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="employee-login-btn"
                className="w-full bg-brand-navy hover:bg-brand-navy-light text-white h-11"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Info Card */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-brand-gold mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-brand-navy">Atharva Finance Solutions</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Use the credentials provided by your HR department
                  </p>
                </div>
              </div>
            </div>

            {/* Staff Login Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Are you a staff member?{' '}
                <Link to="/login" className="text-brand-navy hover:text-brand-gold font-medium transition-colors">
                  Staff Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeLoginPage;
